import { Typography, TypographyProps } from "@mui/material";
import styled from "@mui/material/styles/styled";

import Stack from "@rui/Stack";

import { Moment as MomentType } from "./types";

const TimestampTypography = styled(Typography)(({ theme }) => ({
  fontFamily: theme.typography.fontFamilySecondary,
  color: theme.palette.shades.neutral[600],
  fontSize: "0.75rem",
}));

const Container = styled(Stack)(({ theme, onClick }) => ({
  border: `solid 1px ${theme.palette.shades.neutral[100]}`,
  borderRadius: "4px",
  padding: theme.spacing(1),
  width: "fit-content",
  "&:hover": onClick
    ? {
        cursor: "pointer",
        backgroundColor: "#fafafa",
        borderColor: theme.palette.shades.neutral[200],
      }
    : { cursor: "default" },
}));

interface InstanceProps extends TypographyProps {
  type?: "negative" | "positive" | "neutral" | "question";
  orientation: "horizontal" | "vertical";
}

const Instance = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "type" && prop !== "orientation",
})<InstanceProps>(({ theme, type = "neutral", color, orientation }) => ({
  width: "fit-content",
  whiteSpace: orientation === "vertical" ? undefined : "nowrap",
  fontFamily: theme.typography.fontFamilySecondary,
  fontSize: "0.875rem",
  fontWeight: 500,
  ...(type === "negative" && {
    backgroundColor: theme.palette.shades.negative[500],
    color: "#fff",
  }),
  ...(type === "neutral" && {
    backgroundColor: theme.palette.shades.ghost[500],
    color: theme.palette.shades.neutral[600],
  }),
  ...(type === "question" && {
    backgroundColor: theme.palette.shades.primary[500],
    color: "#FFF",
  }),
  ...(color && {
    backgroundColor: color as React.CSSProperties["color"],
    color: "#FFF",
  }),
  borderRadius: "4px",
  padding: `${theme.spacing(0.05)} ${theme.spacing(0.5)}`,
}));

interface MomentProps extends MomentType {
  onClick?: () => void;
  orientation: "horizontal" | "vertical";
}

const Moment = ({
  instances,
  onClick,
  orientation,
  timestamp,
}: MomentProps) => (
  <Container onClick={onClick} spacing={0.5}>
    <TimestampTypography>{timestamp}</TimestampTypography>
    <Stack
      direction={orientation === "vertical" ? "column" : "row"}
      spacing={0.5}
      sx={{ minWidth: "114px", width: "fit-content" }}
    >
      {instances.map((instance) => (
        <Instance key={instance.label} {...instance} orientation={orientation}>
          {instance.label} {(instance.count ?? 0) > 1 && `(${instance.count})`}
        </Instance>
      ))}
    </Stack>
  </Container>
);

export default Moment;
